import type { FlagKey } from "@amtana/flags";
import type { Language, SupportedLanguage, Words } from "@amtana/i18n";
import type { ComponentType, ReactNode } from "react";

import type { ErrorCopy } from "./errors.ts";

// A module's browser half is a declaration: the screens it serves, the layouts
// it lends, the navigation entries it adds. It is the package's only export
// (`./declaration`). The runtime merges every installed declaration and refuses
// a path, a layout or a navigation id that two modules claim.

export type ScreenLoader = () => Promise<{ readonly default: ComponentType }>;
export type LayoutLoader = () => Promise<{
  readonly default: ComponentType<{ readonly children: ReactNode }>;
}>;

/** Open Graph's own `og:type`; "website" fits every page that is not an article. */
export type SeoType = "website" | "article";

/** A rough content-type profile a future readability or keyword gate may read a page by. */
export type SeoContentType = "landing" | "legal" | "article";

/** One schema.org block (Organization, WebSite, SoftwareApplication, …), plain data. */
export type SeoJsonLdBlock = Readonly<Record<string, unknown>>;

/** Structured data for a page, read in the active language (a name or description may carry
 * `{product}`-filled words); `@amtana/browser`'s `search-head.ts` renders each block as its own
 * `<script type="application/ld+json">`. */
export type SeoJsonLd = (language: Language) => readonly SeoJsonLdBlock[];

export type SeoAlternateLink = {
  readonly type: string;
  /** Site-relative or absolute; rendered absolute. */
  readonly href: string;
  readonly title: string;
};

/**
 * ADR-027 point 2 (epic 31, S31.01): what a public website screen carries next to its title, so
 * the prerendered page has a real search head instead of one description serving every page.
 * `@amtana/browser`'s `search-head.ts` renders it; `@amtana/architecture-enforcer` refuses a
 * screen of a module installed in the website (its browser half's `apps` names "website") that
 * carries no `seo`, except the catch-all `"*"` screen.
 */
export type SeoDeclaration = {
  /** 50 to 160 characters once read in a language; a phrase of the module's catalogue, like `title`. */
  readonly description: Words;
  /** The phrase this page is written for (home: about Instagram leads); absent pages carry none. */
  readonly keyphrase?: Words;
  /** An absolute URL or a site-relative path ("/share.png"); absent falls back to the site's own default. */
  readonly image?: string;
  readonly type?: SeoType;
  readonly jsonLd?: SeoJsonLd;
  /** Other forms of this page or its section, read in the active language: a feed, a markdown twin.
   * `search-head.ts` renders each as `<link rel="alternate">` with its own type (ADR-029 point 3). */
  readonly alternateLinks?: (language: Language) => readonly SeoAlternateLink[];
  /** Whether the build lists this page in `sitemap.xml`; default true. */
  readonly sitemap?: boolean;
  /** `<meta name="robots" content="noindex">`; absent indexes the page. */
  readonly noindex?: boolean;
  readonly contentType?: SeoContentType;
  /** `meta keywords`, and an `article:tag` each on an article: a site that already carries them keeps them. */
  readonly keywords?: readonly string[];
  /** The page's content date when it is not its files' last commit: a moved page keeps its age (D1265).
   * `null`: nobody knows it, so the sitemap names no date rather than a false one (D1347). */
  readonly lastmod?: string | null;
  /** The repository files this page's content comes from (ADR-028 point 1): the website build
   * dates the page's sitemap `<lastmod>` by their last commit. Absent, a page carries the build's
   * own time (a generated listing has no single source). */
  readonly sources?: readonly string[];
};

export type ScreenDeclaration = {
  /** An exact path, a pattern with `:params` (`/analysis/:analysisId`), or "*" for the catch-all. */
  readonly path: string;
  /** The document title while this screen is open: a `phrase(...)` of the module's catalogue. */
  readonly title: Words;
  /** This screen's search head (ADR-027 point 2); required for a public website screen. */
  readonly seo?: SeoDeclaration;
  /** The languages this page exists in (ADR-029 point 2); absent means every shipped language. In
   * any other language the path answers as if no screen declared it. */
  readonly languages?: readonly SupportedLanguage[];
  readonly load: ScreenLoader;
  /** The layout the screen renders inside, by name; a module lends it with withLayouts. */
  readonly layout?: string;
  /**
   * ADR-006 §7: the flag this screen needs. Off for the active organization, or not yet
   * known, the real screen never renders: nothing while the flags are still loading, the
   * not-found screen once they have loaded and it is off (`flag-gate.ts`).
   */
  readonly flag?: FlagKey;
  /**
   * S12.01: the permission this screen needs (a catalogued `"<resource>:<action>"` string). Like
   * `flag`, it is never drawn before the session's permissions arrive (nothing while they load),
   * and opens as the not-found screen once they have arrived without it (`access-gate.ts`), so a
   * staff-only page leaves no trace for anyone else.
   */
  readonly requiresPermission?: string;
};

export type LayoutDeclaration = { readonly load: LayoutLoader };

export type LendLoader = () => Promise<{ readonly default: unknown }>;
export type LendDeclaration = { readonly load: LendLoader };

/**
 * Where a navigation entry sits; the app's chrome decides how groups render.
 * `settings-*` groups make up the Settings sidebar, `back-office*` the back office's own
 * sidebar (S12.02); `utilities` sits at the sidebar's foot, just above Settings (S12.01);
 * `support` fills the Support menu.
 */
export type NavigationGroup =
  | "main"
  | "leads"
  | "outreach"
  | "settings-you"
  | "settings-workspace"
  | "settings-enterprise"
  | "settings-billing"
  | "settings-rewards"
  | "back-office"
  | "back-office-tenants"
  | "back-office-platform"
  | "back-office-operations"
  | "utilities"
  | "support";

export type NavigationEntry = {
  readonly id: string;
  readonly group: NavigationGroup;
  /** A `phrase(...)` of the module's catalogue, read in the active language. */
  readonly label: Words;
  readonly href: string;
  /** A design-system icon name. */
  readonly icon: string;
  readonly order: number;
  readonly external?: boolean;
  /** Extra path prefixes that mark this entry active (e.g. "/analysis/" for Analyses). */
  readonly activeFor?: readonly string[];
  /** Shows only to a session holding this permission (a catalogued `"<resource>:<action>"`
   * string; this package names no module's types), checked against `auth.session`'s own live
   * set (S9.01). Absent shows the entry to every signed-in person, as before. */
  readonly requiresPermission?: string;
  /** ADR-006 §7: the flag this entry needs; absent while it has not resolved to on. */
  readonly flag?: FlagKey;
};

/** A navigation entry as a screen shows it: its label read in the active language (`useNavigation`). */
export type ShownNavigationEntry = NavigationEntry & { readonly label: string };

/** Every organization-scoped flag's resolved value, by key; absent means not loaded yet. */
export type FlagValues = Readonly<Record<string, boolean>>;

/**
 * The platform app's own read of the active organization's flags (ADR-006 §7): a hook,
 * supplied by the one module that owns fetching it (`feature-flag`'s browser half, via
 * `withFlagsSource`), that `createUi` calls once at the root and hands every screen and
 * every navigation entry through `FlagValuesContext`.
 */
export type FlagsSource = () => FlagValues | undefined;

/**
 * S12.01: the session's live permission set, undefined until it has loaded: a hook, supplied by
 * the one module that owns the session (`auth`'s browser half, via `withPermissionsSource`), that
 * `createUi` calls once at the root, the same way it reads the flags source. A screen or a
 * navigation entry naming `requiresPermission` is gated on it.
 */
export type PermissionsSource = () => readonly string[] | undefined;

/** A file a website's build writes beside its pages, relative to its output. */
export type SiteFile = { readonly file: string; readonly content: string };

/**
 * ADR-035, D1262: the files a module's site writes for machines (`robots.txt`, twins, `llms.txt`),
 * given the ones the build already made. Loaded by the build alone, so a bundle never carries them.
 */
export type SiteFilesSource = (built: readonly SiteFile[]) => Promise<readonly SiteFile[]>;

export type BrowserModule = {
  readonly kind: "browser-module";
  readonly id: string;
  readonly screens: Readonly<Record<string, ScreenDeclaration>>;
  readonly layouts: Readonly<Record<string, LayoutDeclaration>>;
  readonly navigation: readonly NavigationEntry[];
  /** Components lent to other modules, keyed by token id (`<owner>.<name>`). */
  readonly lends: Readonly<Record<string, LendDeclaration>>;
  /** What the user reads for each of the module's error codes (packages/module ADR-001). */
  readonly errors: Readonly<Record<string, ErrorCopy>>;
  /** ADR-006 §7: the active organization's flags, if this module is the one that supplies them. */
  readonly flagsSource?: FlagsSource;
  /** S12.01: the session's permissions, if this module is the one that supplies them. */
  readonly permissionsSource?: PermissionsSource;
  /** The files this module's site writes for machines, if it writes any. */
  readonly siteFiles?: SiteFilesSource;
};

type BrowserInstaller = BrowserModule & {
  withScreens(screens: Readonly<Record<string, ScreenDeclaration>>): BrowserInstaller;
  withLayouts(layouts: Readonly<Record<string, LayoutDeclaration>>): BrowserInstaller;
  withNavigation(entries: readonly NavigationEntry[]): BrowserInstaller;
  /** Lends a component by its token; the loaded default must accept the token's props. */
  withLends(
    lends: ReadonlyArray<{ readonly token: { readonly id: string }; readonly load: LendLoader }>,
  ): BrowserInstaller;
  /** Copy for the module's error codes: `satisfies ErrorCopyFor<typeof X_ERROR_CODES>`, so none is missed. */
  withErrorCopy(copy: Readonly<Record<string, ErrorCopy>>): BrowserInstaller;
  /** The one hook that reads the active organization's flags; `install` refuses a second module naming one. */
  withFlagsSource(source: FlagsSource): BrowserInstaller;
  /** The one hook that reads the session's permissions; `install` refuses a second module naming one. */
  withPermissionsSource(source: PermissionsSource): BrowserInstaller;
  withSiteFiles(source: SiteFilesSource): BrowserInstaller;
};

function installer(state: BrowserModule): BrowserInstaller {
  return Object.freeze({
    ...state,
    withScreens: (screens: Readonly<Record<string, ScreenDeclaration>>) =>
      installer({ ...state, screens: { ...state.screens, ...screens } }),
    withLayouts: (layouts: Readonly<Record<string, LayoutDeclaration>>) =>
      installer({ ...state, layouts: { ...state.layouts, ...layouts } }),
    withNavigation: (entries: readonly NavigationEntry[]) =>
      installer({ ...state, navigation: [...state.navigation, ...entries] }),
    withLends: (
      lends: ReadonlyArray<{ readonly token: { readonly id: string }; readonly load: LendLoader }>,
    ) => {
      const next: Record<string, LendDeclaration> = { ...state.lends };
      for (const lend of lends) {
        if (!lend.token.id.startsWith(`${state.id}.`)) {
          throw new Error(
            `"${state.id}" cannot lend "${lend.token.id}": a module lends only its own tokens.`,
          );
        }
        next[lend.token.id] = { load: lend.load };
      }
      return installer({ ...state, lends: next });
    },
    withErrorCopy: (copy: Readonly<Record<string, ErrorCopy>>) =>
      installer({ ...state, errors: { ...state.errors, ...copy } }),
    withFlagsSource: (source: FlagsSource) => installer({ ...state, flagsSource: source }),
    withPermissionsSource: (source: PermissionsSource) => installer({ ...state, permissionsSource: source }),
    withSiteFiles: (source: SiteFilesSource) => installer({ ...state, siteFiles: source }),
  });
}

export function defineBrowserModule<const TId extends string>(id: TId): BrowserInstaller {
  return installer({
    kind: "browser-module",
    id,
    screens: {},
    layouts: {},
    navigation: [],
    lends: {},
    errors: {},
  });
}
